import type {
  CustomerDailyMetrics,
  CustomerListItem,
  CustomerDetail,
  StrategySummary,
} from "../data/api-types";
import { mockSignals } from "./signals";
import { mockAlerts } from "./alerts";
import { mockOpportunities } from "./opportunities";
import { mockTimelineEvents } from "./timeline";
import { mockNotes } from "./notes";
import { mockToday as today, daysAgo } from "./today";

function makeMetrics(
  overrides: Partial<CustomerDailyMetrics> &
    Pick<CustomerDailyMetrics, "id" | "customer_id">,
): CustomerDailyMetrics {
  return {
    as_of_date: today,
    average_days_between_orders_last_12_months: 0,
    seasonal_peak_month: null,
    days_until_seasonal_peak_month_start: null,
    average_order_value_last_90_days: 0,
    average_order_value_previous_90_days: 0,
    days_since_last_order: null,
    improvement_log_count_last_30_days: 0,
    improvement_log_count_last_90_days: 0,
    order_count_last_30_days: 0,
    order_count_last_90_days: 0,
    order_count_previous_90_days: 0,
    revenue_last_12_months: 0,
    revenue_last_30_days: 0,
    revenue_last_90_days: 0,
    revenue_last_90_days_last_year: 0,
    revenue_next_90_days_last_year: 0,
    revenue_previous_90_days: 0,
    work_category_id: null,
    count_open_estimates: 0,
    count_open_estimates_aged_gt_14d: 0,
    total_open_estimate_value: 0,
    count_estimates_won_last_90d: 0,
    count_estimates_lost_last_90d: 0,
    count_estimates_lost_aged_90_to_180d: 0,
    count_live_orders: 0,
    total_live_order_value: 0,
    distinct_work_categories_last_12_months: 0,
    open_issue_count_last_90d: 0,
    open_complaint_count_last_90d: 0,
    has_calibratable_miscellaneous_pool: false,
    ...overrides,
  };
}

// View-module convention (api-restructure PR 4): every composed
// customer-list item carries ``type`` and ``_self`` at the top level.
// The helper wraps the bare mock entries below so each literal stays
// focused on its unique data rather than repeating boilerplate.
type _BareMockCustomer = Omit<CustomerListItem, "type" | "_self">;

function _withViewFields(c: _BareMockCustomer): CustomerListItem {
  return {
    type: "customer",
    _self: `/views/customer-360/${c.id}`,
    ...c,
  };
}

// Helper for building mock strategy summaries. Keeps the literals below
// focused on the distinguishing fields rather than repeating the health
// factor score keys. Distribute across tier bands so the strategy-view
// stories and filter pills have data for every option.
const SIGNAL_NAMES = [
  "Account health: sales trend",
  "Account health: buying recency",
  "Account health: contact coverage",
  "Account health: product breadth",
  "Account health: risk flags",
] as const;
const WEIGHTS = [30, 20, 20, 15, 15] as const;

function _makeStrategySummary(input: {
  wallet_gbp: number | null;
  revenue_12m: number;
  coverage: "absent" | "patchy" | "adequate" | "good" | null;
  strategic_key_contacts_count: number;
  factor_scores: [
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
  ];
}): StrategySummary {
  const untappedGbp =
    input.wallet_gbp == null
      ? null
      : Math.max(input.wallet_gbp - input.revenue_12m, 0);
  const untappedTier =
    untappedGbp == null
      ? null
      : untappedGbp <= 0
        ? "tapped_out"
        : untappedGbp < 10_000
          ? "token"
          : untappedGbp < 50_000
            ? "workable"
            : untappedGbp < 100_000
              ? "meaningful"
              : untappedGbp < 250_000
                ? "strategic"
                : "whale";
  const coverageLabel =
    input.coverage === "good"
      ? "Good"
      : input.coverage === "adequate"
        ? "Adequate"
        : input.coverage === "patchy"
          ? "Patchy"
          : input.coverage === "absent"
            ? "Unknown"
            : null;
  // Weighted mean over non-null factor scores (mirrors the frontend helper).
  let totalWeight = 0;
  let weightedSum = 0;
  input.factor_scores.forEach((s, i) => {
    if (s != null) {
      totalWeight += WEIGHTS[i];
      weightedSum += s * WEIGHTS[i];
    }
  });
  const healthScore =
    totalWeight === 0 ? null : Math.round(weightedSum / totalWeight);
  const healthTier =
    healthScore == null
      ? "unknown"
      : healthScore >= 80
        ? "thriving"
        : healthScore >= 60
          ? "healthy"
          : healthScore >= 40
            ? "watch"
            : "at_risk";
  const factorScoresByName: Record<string, number | null> = {};
  SIGNAL_NAMES.forEach((name, i) => {
    factorScoresByName[name] = input.factor_scores[i];
  });
  return {
    wallet_gbp: input.wallet_gbp,
    untapped_gbp: untappedGbp,
    untapped_tier: untappedTier,
    coverage: input.coverage,
    coverage_tier_label: coverageLabel,
    strategic_key_contacts_count: input.strategic_key_contacts_count,
    health_score: healthScore,
    health_tier: healthTier,
    account_health_factor_scores: factorScoresByName,
    // No fixture starts overridden — the mock adapter's
    // mockCustomerHealthTierOverrides map is empty by default and only the
    // customer-360 detail path (getCustomerById) currently reflects a set
    // override; this list-fixture field exists only to satisfy the schema.
    health_tier_is_overridden: false,
    health_tier_override_reason: null,
    health_tier_override_expires_at: null,
  };
}

const _rawMockCustomers: _BareMockCustomer[] = [
  {
    id: "cust_012",
    name: "Bramley & Co",
    customer_code: "BRAM",
    sales_rep: "Dana Okafor",
    csr: "Priya Shah",
    market: "uk",
    segment_id: "strategic",
    metrics: makeMetrics({
      id: "m-cust_012-1",
      customer_id: "cust_012",
      revenue_last_30_days: 4200,
      revenue_last_90_days: 12800,
      revenue_last_90_days_last_year: 11500,
      revenue_previous_90_days: 13200,
      revenue_last_12_months: 51200,
      average_order_value_last_90_days: 2100,
      average_order_value_previous_90_days: 2200,
      order_count_last_30_days: 2,
      order_count_last_90_days: 6,
      order_count_previous_90_days: 6,
      average_days_between_orders_last_12_months: 18.2,
      days_since_last_order: 14,
    }),
    open_alerts_count: 6,
    pending_opportunities_count: 1,
    open_tickets_count: 1,
    strategy_summary: _makeStrategySummary({
      wallet_gbp: 180_000,
      revenue_12m: 51_200,
      coverage: "good",
      strategic_key_contacts_count: 4,
      factor_scores: [60, 55, 75, 55, 70],
    }),
  },
  {
    id: "cust_022",
    name: "TechStart Ltd",
    customer_code: "TECH",
    sales_rep: "Marcus Webb",
    csr: "Priya Shah",
    market: "uk",
    segment_id: "key",
    metrics: makeMetrics({
      id: "m-cust_022-1",
      customer_id: "cust_022",
      revenue_last_30_days: 8500,
      revenue_last_90_days: 22300,
      revenue_last_12_months: 88000,
      average_order_value_last_90_days: 3700,
      order_count_last_30_days: 3,
      order_count_last_90_days: 6,
      average_days_between_orders_last_12_months: 22.0,
      days_since_last_order: 5,
    }),
    open_alerts_count: 2,
    pending_opportunities_count: 3,
    open_tickets_count: 3,
    strategy_summary: _makeStrategySummary({
      wallet_gbp: 110_000,
      revenue_12m: 88_000,
      coverage: "patchy",
      strategic_key_contacts_count: 2,
      factor_scores: [45, 40, 35, 55, 50],
    }),
    // Visibility-provenance example (docs/features/csr-account-allocation/)
    // — a catch-all/House Account customer, visible to every CSR rather
    // than scoped to one portfolio.
    visibility_reason: "catch_all",
  },
  // Order to invoice: the customer behind orders ORD-K48193, ORD-K48197, ORD-K48249 and ORD-K48254 (mock-data/orders.ts).
  {
    id: "cust_acme",
    name: "Acme Ltd",
    customer_code: "ACME",
    sales_rep: "Marcus Webb",
    csr: "Priya Shah",
    market: "uk",
    segment_id: "client",
    metrics: makeMetrics({
      id: "m-cust_acme-1",
      customer_id: "cust_acme",
      revenue_last_30_days: 1450,
      revenue_last_90_days: 4100,
      revenue_last_12_months: 16800,
      average_order_value_last_90_days: 820,
      order_count_last_30_days: 2,
      order_count_last_90_days: 5,
      average_days_between_orders_last_12_months: 30.5,
      days_since_last_order: 0,
    }),
    open_alerts_count: 0,
    pending_opportunities_count: 0,
    open_tickets_count: 0,
    strategy_summary: _makeStrategySummary({
      wallet_gbp: 40000,
      revenue_12m: 16800,
      coverage: "patchy",
      strategic_key_contacts_count: 1,
      factor_scores: [50, 45, 50, 55, 50],
    }),
  },
  // Order to invoice: project PRJ-D30515 (Travelodge AW26), billed through its central payer HH Associates (mock-data/orders.ts, ORD-K48239–ORD-K48245).
  {
    id: "cust_travelodge",
    name: "Travelodge",
    customer_code: "TRAV",
    sales_rep: "Marcus Webb",
    csr: "Priya Shah",
    market: "uk",
    segment_id: "client",
    metrics: makeMetrics({
      id: "m-cust_travelodge-1",
      customer_id: "cust_travelodge",
      revenue_last_30_days: 1800,
      revenue_last_90_days: 5200,
      revenue_last_12_months: 21000,
      average_order_value_last_90_days: 1730,
      order_count_last_30_days: 3,
      order_count_last_90_days: 3,
      average_days_between_orders_last_12_months: 21.0,
      days_since_last_order: 0,
    }),
    open_alerts_count: 0,
    pending_opportunities_count: 0,
    open_tickets_count: 0,
    strategy_summary: _makeStrategySummary({
      wallet_gbp: 60000,
      revenue_12m: 21000,
      coverage: "patchy",
      strategic_key_contacts_count: 1,
      factor_scores: [50, 45, 50, 55, 50],
    }),
  },
  // Order to invoice: the café behind order ORD-K48229, three versions to three sites (mock-data/orders.ts).
  {
    id: "cust_harbour",
    name: "Harbour Café",
    customer_code: "HARB",
    sales_rep: "Marcus Webb",
    csr: "Priya Shah",
    market: "uk",
    segment_id: "client",
    metrics: makeMetrics({
      id: "m-cust_harbour-1",
      customer_id: "cust_harbour",
      revenue_last_30_days: 516,
      revenue_last_90_days: 860,
      revenue_last_12_months: 3900,
      average_order_value_last_90_days: 860,
      order_count_last_30_days: 1,
      order_count_last_90_days: 1,
      average_days_between_orders_last_12_months: 88.0,
      days_since_last_order: 10,
    }),
    open_alerts_count: 0,
    pending_opportunities_count: 0,
    open_tickets_count: 0,
    strategy_summary: _makeStrategySummary({
      wallet_gbp: 9000,
      revenue_12m: 3900,
      coverage: "patchy",
      strategic_key_contacts_count: 1,
      factor_scores: [50, 45, 50, 55, 50],
    }),
  },
  {
    id: "cust_048",
    name: "Business School UK",
    customer_code: "BSCH001",
    sales_rep: "Dana Okafor",
    csr: "Leah Grant",
    market: "uk",
    segment_id: "strategic",
    metrics: makeMetrics({
      id: "m-cust_048-1",
      customer_id: "cust_048",
      revenue_last_30_days: 18200,
      revenue_last_90_days: 47600,
      revenue_last_90_days_last_year: 52400,
      revenue_previous_90_days: 49800,
      revenue_last_12_months: 192000,
      average_order_value_last_90_days: 5400,
      order_count_last_30_days: 4,
      order_count_last_90_days: 9,
      average_days_between_orders_last_12_months: 12.4,
      days_since_last_order: 3,
    }),
    open_alerts_count: 4,
    pending_opportunities_count: 2,
    open_tickets_count: 2,
    strategy_summary: _makeStrategySummary({
      wallet_gbp: 520_000,
      revenue_12m: 192_000,
      coverage: "good",
      strategic_key_contacts_count: 7,
      factor_scores: [85, 90, 80, 75, 85],
    }),
  },
  {
    id: "cust_073",
    name: "Henderson Group",
    customer_code: "HEND",
    sales_rep: "Marcus Webb",
    csr: "Leah Grant",
    market: "uk",
    segment_id: "client",
    metrics: makeMetrics({
      id: "m-cust_073-1",
      customer_id: "cust_073",
      revenue_last_30_days: 1200,
      revenue_last_90_days: 4400,
      revenue_last_12_months: 22000,
      average_order_value_last_90_days: 800,
      order_count_last_30_days: 1,
      order_count_last_90_days: 5,
      average_days_between_orders_last_12_months: 35.0,
      days_since_last_order: 27,
    }),
    open_alerts_count: 1,
    pending_opportunities_count: 0,
    open_tickets_count: 0,
    strategy_summary: _makeStrategySummary({
      wallet_gbp: 28_000,
      revenue_12m: 22_000,
      coverage: "absent",
      strategic_key_contacts_count: 0,
      factor_scores: [25, 20, 10, 30, 15],
    }),
  },
  // Anchor fixture for the job workspace redesign (docs/features/
  // job-workspace-redesign/) — the real customer on job 738492.
  {
    id: "c-capture-property",
    name: "Capture Property Marketing Ltd",
    customer_code: "CAP9092",
    sales_rep: "Dana Okafor",
    csr: "Stuart Russell",
    market: "uk",
    segment_id: "client",
    metrics: makeMetrics({
      id: "m-c-capture-property-1",
      customer_id: "c-capture-property",
      revenue_last_30_days: 640,
      revenue_last_90_days: 2100,
      revenue_last_12_months: 8400,
      average_order_value_last_90_days: 210,
      order_count_last_30_days: 1,
      order_count_last_90_days: 6,
      average_days_between_orders_last_12_months: 21.0,
      days_since_last_order: 3,
    }),
    open_alerts_count: 0,
    pending_opportunities_count: 0,
    open_tickets_count: 0,
    strategy_summary: _makeStrategySummary({
      wallet_gbp: 12_000,
      revenue_12m: 8_400,
      coverage: "patchy",
      strategic_key_contacts_count: 2,
      factor_scores: [55, 60, 50, 55, 50],
    }),
    // Visibility-provenance example — this CSR's absence-cover buddy is
    // temporarily seeing their portfolio (work_visibility_module's
    // system-sourced cover).
    visibility_reason: "covering",
    covering_for_user_name: "Stuart Russell",
    cover_until: daysAgo(-5),
  },
  // The portal-access journeys' customers (docs/features/customer-storefront/
  // 12_decision_access_for_every_customer.md): a corporate group with several
  // accounts, three clients one agency acts for, and a customer whose portal
  // staff have blocked. People for each live in customer-portal-access.ts.
  portalAccessCustomer("cust_epwin", "Epwin Group", "EPWIN", "strategic"),
  portalAccessCustomer("cust_currys", "Currys", "CURR01", "key"),
  portalAccessCustomer("cust_pret", "Pret A Manger", "PRET01", "key"),
  portalAccessCustomer("cust_petsathome", "Pets at Home", "PETS01", "key"),
  portalAccessCustomer(
    "cust_brightwater",
    "Brightwater Estates",
    "BRIG01",
    "client",
  ),
];

function portalAccessCustomer(
  id: string,
  name: string,
  customerCode: string,
  segmentId: string,
): _BareMockCustomer {
  return {
    id,
    name,
    customer_code: customerCode,
    sales_rep: "Dana Okafor",
    csr: "Casey CSR",
    market: "uk",
    segment_id: segmentId,
    metrics: makeMetrics({
      id: `m-${id}-1`,
      customer_id: id,
      revenue_last_30_days: 5200,
      revenue_last_90_days: 15800,
      revenue_last_12_months: 61000,
      average_order_value_last_90_days: 1300,
      order_count_last_30_days: 4,
      order_count_last_90_days: 12,
      average_days_between_orders_last_12_months: 14.0,
      days_since_last_order: 6,
    }),
    open_alerts_count: 0,
    pending_opportunities_count: 0,
    open_tickets_count: 0,
    strategy_summary: _makeStrategySummary({
      wallet_gbp: 120_000,
      revenue_12m: 61_000,
      coverage: "adequate",
      strategic_key_contacts_count: 2,
      factor_scores: [65, 70, 60, 60, 70],
    }),
  };
}

export const mockCustomers: CustomerListItem[] =
  _rawMockCustomers.map(_withViewFields);

export const mockCustomerListItem: CustomerListItem = mockCustomers[0]!;

export const mockCustomerDetail: CustomerDetail = {
  type: "customer_360",
  id: "cust_048",
  _self: "/views/customer-360/cust_048",
  customer: {
    id: "cust_048",
    name: "Business School UK",
    market: "uk",
    sales_rep: "",
    csr: "",
    customer_code: "BSCH001",
    posting_account: "BSCH001",
    // The mock's CIS ids and page ids are one and the same.
    cis_customer_id: "cust_048",
  },
  // Billing group (Tharstern PostingAccount) — this account is the head of a
  // 3-account group; members link to real mock customers for click-through.
  group: {
    head_code: "BSCH001",
    head_name: "Business School UK",
    head_customer_id: "cust_048",
    member_count: 3,
    members: [
      {
        customer_id: "cust_048",
        code: "BSCH001",
        name: "Business School UK",
        is_head: true,
      },
      {
        customer_id: "cust_012",
        code: "BRAM001",
        name: "Bramley & Co",
        is_head: false,
      },
      {
        customer_id: "cust_073",
        code: "HEND001",
        name: "Henderson Group",
        is_head: false,
      },
    ],
  },
  website: "https://www.businessschool.ac.uk",
  description: "UK business school with in-house print and design team.",
  sector: "Education",
  uk_turnover_gbp: 7_500_000,
  uk_turnover_as_of: `${today}T08:00:00Z`,
  uk_turnover_confidence: "medium",
  uk_employees: 150,
  uk_employees_as_of: `${today}T08:00:00Z`,
  uk_employees_confidence: "medium",
  uk_wallet_gbp: 180_000,
  uk_wallet_as_of: `${today}T08:00:00Z`,
  uk_wallet_confidence: "medium",
  last_enriched_at: `${today}T08:00:00Z`,
  // Visibility-provenance example on customer-360 — see the list-item
  // examples above for the same convention.
  visibility_reason: "covering",
  covering_for_user_name: "Jordan Pierce",
  cover_until: daysAgo(-3),
  strategic_intelligence: {
    centralised_print_control_appetite: "high",
    centralised_print_control_rationale:
      "Brand team owns procurement across the estate.",
    contracted_relationship_appetite: "medium",
    contracted_relationship_rationale:
      "Open to framework deals but not ready to commit to exclusivity.",
    commercial_print_and_mailing_estimated_spend_gbp: 110_000,
    commercial_print_and_mailing_estimated_spend_rationale:
      "Annual reports, prospectus reprints, and regular direct-mail campaigns.",
    workwear_estimated_spend_gbp: 35_000,
    workwear_estimated_spend_rationale:
      "Seasonal staff uniform refresh; growing focus on branded outerwear.",
    merch_estimated_spend_gbp: 20_000,
    merch_estimated_spend_rationale:
      "Student onboarding swag packs and open-day giveaways.",
    creative_work_estimated_spend_gbp: 15_000,
    creative_work_estimated_spend_rationale:
      "Small internal design team — outsources overflow for larger campaigns.",
    strategy_summary: "Execute growth",
    overall_opportunity_assessment:
      "Sizeable untapped workwear and merch wallet with adequate ops-level coverage; revenue has held steady through the last cycle, so the play is converting existing access into upside rather than defending share.",
    proposed_account_development_strategy:
      "Lead with managed-print consolidation, then expand into workwear.",
    proposed_upsell_focus_areas:
      "Workwear and branded merch. Annual report production at scale.",
    existing_contact_coverage_quality: "adequate",
    existing_contact_coverage_rationale:
      "Strong coverage at operations level; limited senior access.",
    preserve_and_protect_priority: "high",
    preserve_and_protect_rationale:
      "Long-standing account with steady annual volume — losing it would materially impact the book.",
    upsell_and_grow_priority: "critical",
    upsell_and_grow_rationale:
      "One of the largest addressable workwear gaps in the portfolio; relationship is strong enough to push.",
  },
  contacts: [
    {
      id: "person_1",
      customer_id: "cust_048",
      name: "Sarah Chen",
      email_address: "s.chen@businessschool.ac.uk",
      phone_number_1: "+44 20 7123 4567",
      phone_number_2: "",
      is_invoice_contact: false,
      is_primary_customer_contact: true,
      is_key_decision_maker: true,
      job_title: "Print & Procurement Manager",
      linkedin_url: "https://linkedin.com/in/sarahchen",
      background_summary:
        "Manages print procurement and supplier relationships.",
      current_company_inferred: null,
    },
  ],
  prospect_contacts: [],
  metrics: makeMetrics({
    id: "m-cust_048-1",
    customer_id: "cust_048",
    revenue_last_30_days: 18200,
    revenue_last_90_days: 47600,
    revenue_last_90_days_last_year: 52400,
    revenue_previous_90_days: 49800,
    revenue_last_12_months: 192000,
    average_order_value_last_90_days: 5400,
    order_count_last_30_days: 4,
    order_count_last_90_days: 9,
    average_days_between_orders_last_12_months: 12.4,
    days_since_last_order: 3,
  }),
  segment: { segment_id: "strategic" },
  signals: mockSignals.filter((s) => s.customer_id === "cust_048"),
  alerts: mockAlerts.filter((a) => a.customer_id === "cust_048"),
  opportunities: mockOpportunities.filter((n) => n.customer_id === "cust_048"),
  timeline: mockTimelineEvents.filter((t) => t.customer_id === "cust_048"),
  notes: mockNotes.filter((n) => n.customer_id === "cust_048"),
  campaign_history: [
    {
      id: "touch_048_1",
      campaign_id: "cmp_004",
      campaign_name: "Strategic Retail Check-in",
      person_id: "mp_012",
      contact_name: "Alan Webb",
      contact_email: "alan@webbprintsolutions.co.uk",
      channel: "email",
      status: "sent",
      planned_at: "2026-08-01T09:00:00Z",
      executed_at: "2026-08-01T09:03:00Z",
      outcome: null,
    },
    {
      id: "touch_048_2",
      campaign_id: "cmp_001",
      campaign_name: "Spring Folder Push",
      person_id: "mp_012",
      contact_name: "Alan Webb",
      contact_email: "alan@webbprintsolutions.co.uk",
      channel: "email",
      status: "sent",
      planned_at: "2026-06-02T09:00:00Z",
      executed_at: "2026-06-02T09:00:00Z",
      outcome: "Opened, clicked pricing link",
    },
    {
      id: "touch_048_3",
      campaign_id: "cmp_001",
      campaign_name: "Spring Folder Push",
      person_id: "mp_012",
      contact_name: "Alan Webb",
      contact_email: "alan@webbprintsolutions.co.uk",
      channel: "call",
      status: "planned",
      planned_at: "2026-08-12T14:00:00Z",
      executed_at: null,
      outcome: null,
    },
  ],
  open_ticket_count: 2,
  next_meeting_at: null,
  // Live (work-in-progress) jobs — computed live from is_wip in production;
  // the mock carries a representative figure for the Pulse KPI + card.
  live_jobs_count: 7,
  live_jobs_value: 41250,
};
